import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle, Ban, Building2, CalendarClock, Hourglass, Loader2, RefreshCw, Route as RouteIcon, ShieldCheck, Truck, UserCheck, Users,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { Page } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { FormAlert } from '@/components/ui/form-alert';
import { Skeleton } from '@/components/ui/skeleton';
import { genericErrorMessage } from '@/lib/api-errors';
import { formatDateTime, formatNumber } from '@/lib/format';
import { METRICS_KEY, METRICS_REFRESH_MS, metricsApi, type PlatformMetrics } from '@/lib/metrics-api';
import type { TenantStatus } from '@/lib/tenants-api';
import { useAuth } from '@/lib/use-auth';
import { cn } from '@/lib/utils';

interface TileDef {
  id: string;
  label: string;
  icon: LucideIcon;
  value: (m: PlatformMetrics) => number;
  /** Tuile cliquable vers la liste filtrée. */
  status?: TenantStatus;
  /** Mise en avant orange si la valeur est > 0. */
  warnIfPositive?: boolean;
  hint?: string;
}

export const TILES: TileDef[] = [
  { id: 'tenants-active', label: 'Tenants actifs', icon: Building2, value: (m) => m.tenants.active, status: 'ACTIVE' },
  { id: 'tenants-trial', label: 'Tenants en essai', icon: Hourglass, value: (m) => m.tenants.trial, status: 'TRIAL' },
  { id: 'tenants-suspended', label: 'Tenants suspendus', icon: Ban, value: (m) => m.tenants.suspended, status: 'SUSPENDED' },
  {
    id: 'trials-expiring',
    label: 'Essais expirant sous 7 jours',
    icon: CalendarClock,
    value: (m) => m.tenants.trialExpiringIn7Days,
    warnIfPositive: true,
    hint: 'À relancer avant la fin de l’essai',
  },
  { id: 'users-total', label: 'Utilisateurs', icon: Users, value: (m) => m.users.total },
  { id: 'users-active', label: 'Connectés (30 derniers jours)', icon: UserCheck, value: (m) => m.users.loggedInLast30Days },
  { id: 'vehicles', label: 'Véhicules', icon: Truck, value: (m) => m.vehicles.total },
  { id: 'missions-active', label: 'Missions actives', icon: RouteIcon, value: (m) => m.missions.active, hint: 'Planifiées ou en cours' },
];

const Tile = ({ def, metrics }: { def: TileDef; metrics?: PlatformMetrics }) => {
  const value = metrics ? def.value(metrics) : undefined;
  const warn = Boolean(def.warnIfPositive && value && value > 0);
  const Icon = def.icon;
  const body: ReactNode = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-muted-foreground">{def.label}</p>
        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', warn ? 'bg-warning/15' : 'bg-brand-violet/10')}>
          <Icon className={cn('h-4 w-4', warn ? 'text-amber-600' : 'text-brand-violet')} aria-hidden />
        </span>
      </div>
      {value === undefined ? (
        <Skeleton className="mt-2 h-8 w-16" />
      ) : (
        <p className={cn('mt-1 text-3xl font-semibold tabular-nums', warn && 'text-amber-600')} data-testid={`tile-${def.id}`}>
          {formatNumber(value)}
        </p>
      )}
      {def.hint && <p className="mt-1 text-xs text-muted-foreground">{def.hint}</p>}
      {warn && (
        <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-xs font-medium text-amber-700">
          <AlertTriangle className="h-3 w-3" aria-hidden />À surveiller
        </span>
      )}
    </>
  );
  const className = cn('block rounded-lg border bg-white p-4 shadow-sm', warn && 'border-warning/50');

  return def.status ? (
    <Link to="/tenants" search={{ status: def.status }} className={cn(className, 'transition-colors hover:border-brand-violet/40 hover:bg-brand-violet/[0.02]')}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
};

export const HomePage = () => {
  const { admin } = useAuth();
  // Rafraîchi toutes les 60 s tant que l'onglet est ouvert, et à la demande
  const query = useQuery({ queryKey: METRICS_KEY, queryFn: metricsApi.get, refetchInterval: METRICS_REFRESH_MS });
  if (!admin) return null;
  const name = [admin.firstName, admin.lastName].filter(Boolean).join(' ');

  return (
    <Page>
      <section className="overflow-hidden rounded-xl bg-gradient-to-br from-brand-violet to-brand-teal p-6 text-white shadow-lg">
        <p className="flex items-center gap-2 text-sm text-white/70">
          <ShieldCheck className="h-4 w-4" aria-hidden />
          {admin.email}
        </p>
        <h1 className="mt-1 text-2xl font-semibold">Bienvenue {name || admin.email}</h1>
        <p className="text-white/80">Vous êtes connecté en tant qu'admin plateforme.</p>
      </section>

      <section className="space-y-3" aria-labelledby="metrics-title">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 id="metrics-title" className="text-lg font-semibold">
              Vue d'ensemble de la plateforme
            </h2>
            {query.dataUpdatedAt > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="metrics-updated">
                Mis à jour le {formatDateTime(new Date(query.dataUpdatedAt).toISOString())} · actualisation auto toutes les 60 s
              </p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()} disabled={query.isFetching}>
            {query.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : <RefreshCw className="mr-2 h-4 w-4" aria-hidden />}
            Actualiser
          </Button>
        </div>

        {query.isError && !query.data && (
          <FormAlert>Impossible de charger les métriques : {genericErrorMessage(query.error).toLowerCase()}</FormAlert>
        )}
        {!(query.isError && !query.data) && (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {TILES.map((def) => (
              <Tile key={def.id} def={def} metrics={query.data} />
            ))}
          </div>
        )}
      </section>
    </Page>
  );
};
